import React, { useEffect, useState } from 'react';

import { LinkProps } from '@soluto-design/link';
import { Button, Icon, Text, Title, TitleProps } from '@soluto-design/react';
import cls from '@soluto-design/styles/cls';

import Rating from '../../atoms/Rating';
import Form, { FormProps } from '../../molecules/Form';
import Modal from '../../molecules/Modal';

export interface ContactTourSectionProps {
    iframeUrl: string;
    bannerText?: string; // Esto aparece en el header cuando el usuario ha rellenado el form
    cta?: LinkProps; // Esto aparece en el header cuando el usuario ha rellenado el form
    backButton?: LinkProps; // Este, cuando el usuario no ha rellenado el form, es primary. Cuando lo ha rellenado, pasaría a secondary porque el primary sería cta
    modalTitle?: TitleProps['component'];
    modalText?: string;
    rating?: number;
    ratingLink?: LinkProps;
    form: FormProps;
}

function ContactTourSection({
    iframeUrl,
    bannerText,
    cta,
    backButton,
    modalTitle,
    modalText,
    rating,
    ratingLink,
    form,
}: ContactTourSectionProps) {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(!form);

    const [modalOpen, setModalOpen] = useState<boolean>(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && !isSubmitted) {
            setModalOpen(true);
        }
    }, [isSubmitted]);

    return (
        <section className="h-screen relative pt-[96px] xl:pt-[112px] pb-lg z-10">
            <div className="container !max-w-none h-full w-full">
                <div className="absolute top-0 left-0 right-0 w-full min-h-[56px] xl:min-h-[72px] flex justify-between items-center py-lg px-margin h-auto gap-x-gutter z-50">
                    <span>
                        <Icon icon="brand-logo" className="w-full !h-24" />
                    </span>

                    <div className="flex gap-24 justify-between items-center">
                        <div
                            className={cls(
                                !isSubmitted
                                    ? 'hidden'
                                    : 'flex gap-x-gutter items-center',
                            )}>
                            <Text
                                component="p"
                                className="hidden lg:block text-b300 text-text-primary">
                                {bannerText}
                            </Text>
                            <Button {...cta} variant="primary" size="large" />
                        </div>

                        <Button
                            variant={!isSubmitted ? 'primary' : 'secondary'}
                            size="large"
                            {...backButton}
                        />
                    </div>
                </div>
                <div className="overflow-hidden rounded-large h-full w-full">
                    <iframe
                        title="Guided Tour"
                        src={iframeUrl}
                        className="border-none w-full h-full"
                        allow="fullscreen"
                    />
                </div>

                {!isSubmitted && (
                    <Modal
                        open={modalOpen}
                        id="guided-tour"
                        disableClose
                        className="!max-w-[100vw] relative !top-0 !translate-y-[96px] !max-h-[calc(100vh-96px)]  xl:!max-w-[782px] xl:!max-h-[80vh] xl:!top-1/2 xl:!-translate-y-1/2 hd:!max-w-[1024px]"
                        classes={{
                            wrapper: '!py-36 !px-24 md:!p-40',
                        }}>
                        {modalTitle && (
                            <Title
                                component={modalTitle}
                                className="text-h300 text-text-primary">
                                {modalTitle}
                            </Title>
                        )}
                        {modalText && (
                            <Text
                                component="p"
                                className="text-b300 text-text-primary mt-xs mb-xg">
                                {modalText}
                            </Text>
                        )}
                        {form && (
                            <Form
                                {...form}
                                className="z-10"
                                theme="dark"
                                onSubmit={() => {
                                    setIsSubmitted(true);
                                }}
                            />
                        )}
                        {rating && ratingLink && (
                            <div className="mt-xg">
                                <Rating value={rating} link={ratingLink} />
                            </div>
                        )}
                        <div className="absolute bottom-0 right-0 translate-y-3/4 translate-x-1/2 md:translate-y-1/2 md:translate-x-1/4 z-0">
                            <Icon
                                icon="brand-asset"
                                className="!w-[515px] !h-[515px] text-text-brand-1"
                            />
                        </div>
                    </Modal>
                )}
            </div>
        </section>
    );
}

export default ContactTourSection;
